// frontend/src/pages/AnalysisPage/sections/PartMatching/PartMatching.js
// Purpose: Display and manage part matching
// Desired outcome: Allow users to match parts to categories with confidence control, including both general categories and OEM-specific parts

import React, { useState, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  selectCurrentFile,
  selectPartMatches,
  selectConfidenceThreshold,
  selectProgress,
  selectAvailableCategories,
  selectOemParts,
  selectLoadingOemParts,
  fetchSuggestedPartMatches,
  selectBootstrapInProgress,
  selectBootstrapComplete,
  bootstrapAnalysisData,
  updateConfidenceThreshold, // Using the renamed reducer action here
  setConfidenceThreshold, // Using the thunk here
  storeManualMatches,
  confirmPartMatches,
  updatePartMatch,
  fetchAllOemParts
} from '../../../../state/analysis/analysisSlice';
import MatchStatistics from '../../../../components/MatchStatistics/MatchStatistics';
import logger from '../../../../utils/logger';
import '../sections.css';
import './PartMatching.css';

const PartMatching = () => {
  const dispatch = useDispatch();
  const currentFile = useSelector(selectCurrentFile);
  const partMatches = useSelector(selectPartMatches);
  const confidenceThreshold = useSelector(selectConfidenceThreshold);
  const progress = useSelector(selectProgress);
  const availableCategories = useSelector(selectAvailableCategories);
  const bootstrapInProgress = useSelector(selectBootstrapInProgress);
  const bootstrapComplete = useSelector(selectBootstrapComplete);
  const oemParts = useSelector(selectOemParts);
  const loadingOemParts = useSelector(selectLoadingOemParts);
  
  // Local state for UI
  const [localThreshold, setLocalThreshold] = useState(confidenceThreshold);
  const [loading, setLoading] = useState(false);
  const [error, setLocalError] = useState(null);
  const [filterCategory, setFilterCategory] = useState('all');
  const [filterConfidence, setFilterConfidence] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [editedMatches, setEditedMatches] = useState({});
  
  // Use refs to track initialization
  const hasInitializedPartMatches = useRef(false);
  
  // Set local threshold when the Redux state changes
  useEffect(() => {
    setLocalThreshold(confidenceThreshold);
  }, [confidenceThreshold]);
  
  // Trigger bootstrap on mount
  useEffect(() => {
    if (!bootstrapComplete) {
      dispatch(bootstrapAnalysisData());
    }
  }, [dispatch, bootstrapComplete]);

  // Fetch OEM parts on component mount using Redux
  useEffect(() => {
    // Only fetch if we have completed bootstrap
    if (bootstrapComplete) {
      dispatch(fetchAllOemParts());
    }
  }, [dispatch, bootstrapComplete]);
  
  // Handle threshold change
  const handleThresholdChange = (e) => {
    // Keep threshold between 0-100
    const value = Math.min(100, Math.max(0, parseInt(e.target.value) || 0));
    setLocalThreshold(value);
  };
  
  // Apply new threshold
  const applyThreshold = async () => {
    // Skip if no change or no file
    if (localThreshold === confidenceThreshold || !currentFile?.fileID) {
      if (!currentFile?.fileID) {
        setLocalError('No file selected');
      }
      return;
    }
    
    try {
      setLoading(true);
      
      // Update threshold on server using the thunk action
      await dispatch(setConfidenceThreshold({
        fileID: currentFile.fileID,
        confidenceThreshold: localThreshold
      })).unwrap();
      
      // Update local state using the renamed reducer action
      dispatch(updateConfidenceThreshold(localThreshold));
      
      // Reset initialization flag to trigger a refetch
      hasInitializedPartMatches.current = false;
      
      // Fetch matches with new threshold
      await dispatch(fetchSuggestedPartMatches({
        fileID: currentFile.fileID,
        threshold: localThreshold
      })).unwrap();
      
      console.log(`✅ Threshold updated to ${localThreshold}%`);
    } catch (error) {
      setLocalError(error.message || 'Error updating threshold');
      console.error('❌ Error updating threshold:', error);
    } finally {
      setLoading(false);
    }
  };
  
  // Handle category selection for a part
  const handleCategoryChange = async (partID, category) => {
    // Update local state
    setEditedMatches(prev => ({
      ...prev,
      [partID]: category
    }));
    
    // Update Redux state
    dispatch(updatePartMatch({
      partID,
      matchedCategory: category,
      status: 'confirmed'
    }));
    
    try {
      // Store on server
      await dispatch(storeManualMatches({
        fileID: currentFile.fileID,
        manualMatches: [{
          partID,
          matchedCategory: category,
          status: 'confirmed'
        }]
      })).unwrap();
    } catch (error) {
      setLocalError(error.message || 'Error storing manual match');
      console.error('❌ Error storing manual match:', error);
    }
  };
  
  // Confirm all matches and proceed to next step
  const handleConfirmMatches = async () => {
    if (!currentFile?.fileID || !partMatches?.length) {
      setLocalError('No parts to confirm');
      return;
    }
    
    // Check for uncategorized parts
    const uncategorizedParts = partMatches.filter(match => match.matchedCategory === 'Uncategorized');
    if (uncategorizedParts.length > 0) {
      if (!window.confirm(`There are ${uncategorizedParts.length} uncategorized parts. Proceed anyway?`)) {
        return;
      }
    }
    
    try {
      setLoading(true);
      await dispatch(confirmPartMatches({
        fileID: currentFile.fileID,
        partMatches
      })).unwrap();
      
      console.log('✅ Part matches confirmed successfully');
    } catch (error) {
      setLocalError(error.message || 'Error confirming part matches');
      console.error('❌ Error confirming part matches:', error);
    } finally {
      setLoading(false);
    }
  };
  
  // Filter parts based on criteria
  const getFilteredParts = () => {
    if (!partMatches || partMatches.length === 0) return [];
    
    return partMatches.filter(part => {
      // Filter by category
      if (filterCategory !== 'all' && part.matchedCategory !== filterCategory) {
        return false;
      }
      
      // Filter by confidence level
      if (filterConfidence === 'high' && part.confidenceScore < 80) {
        return false;
      } else if (filterConfidence === 'medium' && (part.confidenceScore < 50 || part.confidenceScore >= 80)) {
        return false;
      } else if (filterConfidence === 'low' && part.confidenceScore >= 50) {
        return false;
      } else if (filterConfidence === 'uncategorized' && part.matchedCategory !== 'Uncategorized') {
        return false;
      }
      
      // Filter by search term
      if (searchTerm && !part.originalName.toLowerCase().includes(searchTerm.toLowerCase())) {
        return false;
      }
      
      return true;
    });
  };
  
  // Get confidence level class
  const getConfidenceClass = (score) => {
    if (score >= 80) return 'high-confidence';
    if (score >= 50) return 'medium-confidence';
    return 'low-confidence';
  };
  
  // If still loading or processing
  if (bootstrapInProgress || loading || progress.status === 'loading' || loadingOemParts) {
    return <div className="loading-indicator">Initializing analysis...</div>;
  }
  
  // Process available categories for display
  const processedCategories = [];
  
  // Safely process available categories
  if (availableCategories && Array.isArray(availableCategories)) {
    availableCategories.forEach(category => {
      // If category is an object, extract the name property
      if (category && typeof category === 'object' && category.name) {
        processedCategories.push(category.name);
      } 
      // If category is a string, use it directly
      else if (typeof category === 'string') {
        processedCategories.push(category);
      }
    });
  }
  
  // Combine categories and OEM parts for the dropdown
  const allCategories = [...processedCategories];
  
  // Process OEM parts from Redux state
  if (oemParts && oemParts.length > 0) {
    // Extract name property if available, otherwise use the part directly
    oemParts.forEach(part => {
      if (part && typeof part === 'object' && part.name) {
        allCategories.push(part.name);
      } else if (typeof part === 'string') {
        allCategories.push(part);
      }
    });
  } else {
    // Fallback extraction from partMatches if oemParts is empty
    if (partMatches && partMatches.length > 0) {
      // Get unique categories from part matches
      const uniqueCategories = [...new Set(partMatches.map(match => match.matchedCategory))]
        .filter(cat => cat !== 'Uncategorized')
        .sort();
      allCategories.push(...uniqueCategories);
    }
  }
  
  // Deduplicate the list
  const categoryOptions = [...new Set(allCategories)].sort();
  
  // Check if we have matches
  if (!partMatches || partMatches.length === 0) {
    return (
      <div className="part-matching-section">
        <h2>Part Matching</h2>
        <div className="alert alert-info">
          No part matches available. Please make sure you've completed the previous steps correctly.
        </div>
      </div>
    );
  }
  
  // Get filtered parts for display
  const filteredParts = getFilteredParts();
  
  return (
    <div className="part-matching-section">
      <div className="part-matching-header">
        <h2>Part Matching</h2>
      </div>
      
      {error && (
        <div className="error-message">
          {error}
          <button 
            className="dismiss-button" 
            onClick={() => setLocalError(null)}
            style={{ marginLeft: '10px' }}
          >
            Dismiss
          </button>
        </div>
      )}
      
      <div className="threshold-control">
        <label htmlFor="confidenceThreshold">Confidence Threshold: {localThreshold}%</label>
        <input
          type="range"
          id="confidenceThreshold"
          min="0"
          max="100"
          value={localThreshold}
          onChange={handleThresholdChange}
          className="threshold-slider"
        />
        <input
          type="number"
          value={localThreshold}
          onChange={handleThresholdChange}
          className="threshold-input"
          min="0"
          max="100"
        />
        <button 
          className="secondary-button"
          onClick={applyThreshold}
          disabled={localThreshold === confidenceThreshold}
        >
          Apply
        </button>
        <p className="threshold-description">
          Parts with confidence below threshold will be marked as "Uncategorized"
        </p>
      </div>
      
      {/* Display match statistics */}
      <MatchStatistics 
        partMatches={partMatches} 
        confidenceThreshold={confidenceThreshold} 
      />
      
      {/* Filtering controls */}
      <div className="filter-controls">
        <div className="filter-item">
          <label htmlFor="categoryFilter">Filter by Category:</label>
          <select
            id="categoryFilter"
            value={filterCategory}
            onChange={(e) => setFilterCategory(e.target.value)}
            className="filter-select"
          >
            <option value="all">All Categories</option>
            <option value="Uncategorized">Uncategorized</option>
            {categoryOptions.map((category, index) => (
              <option key={index} value={category}>
                {category}
              </option>
            ))}
          </select>
        </div>
        
        <div className="filter-item">
          <label htmlFor="confidenceFilter">Filter by Confidence:</label>
          <select
            id="confidenceFilter"
            value={filterConfidence}
            onChange={(e) => setFilterConfidence(e.target.value)}
            className="filter-select"
          >
            <option value="all">All Confidence Levels</option>
            <option value="high">High (80-100%)</option>
            <option value="medium">Medium (50-79%)</option>
            <option value="low">Low (0-49%)</option>
            <option value="uncategorized">Uncategorized Only</option>
          </select>
        </div>
        
        <div className="filter-item">
          <label htmlFor="searchFilter">Search by Name:</label>
          <input
            id="searchFilter"
            type="text"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Search by part name..."
            className="search-input"
          />
        </div>
      </div>
      
      {/* Parts table */}
      <div className="matches-table-container">
      <table className="matches-table">
        <thead>
          <tr>
            <th>Part Name</th>
            <th>Matched Category</th>
            <th>Confidence</th>
            <th>Manual Override</th>
          </tr>
        </thead>
        <tbody>
          {filteredParts.map((part) => (
            <tr key={part.partID}>
              <td>{part.originalName}</td>
              <td>{part.matchedCategory}</td>
              <td className={`confidence-cell ${getConfidenceClass(part.confidenceScore)}`}>
                {part.confidenceScore}%
                <div className="confidence-bar">
                  <div 
                    className="confidence-fill"
                    style={{ width: `${part.confidenceScore}%` }}
                  ></div>
                </div>
              </td>
              <td>
                <select
                  value={editedMatches[part.partID] || part.matchedCategory} 
                  onChange={(e) => handleCategoryChange(part.partID, e.target.value)}
                  className="category-select"
                >
                  <option value="Uncategorized">Uncategorized</option>
                  {/* Group regular categories */}
                  <optgroup label="Standard Categories">
                    {categoryOptions.map((category, index) => (
                      <option key={`standard-${index}`} value={category}>
                        {category}
                      </option>
                    ))}
                  </optgroup>
                  
                  {/* Include any additional categories from part matches that might not be in the above groups */}
                  {partMatches && partMatches.length > 0 && (
                    <optgroup label="Other Categories">
                      {[...new Set(partMatches.map(match => match.matchedCategory))]
                        .filter(cat => 
                          cat !== 'Uncategorized' && 
                          !categoryOptions.includes(cat)
                        )
                        .sort()
                        .map((category, index) => (
                          <option key={`other-${index}`} value={category}>
                            {category}
                          </option>
                        ))
                      }
                    </optgroup>
                  )}
                </select>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      </div>
      
      <div className="actions">
        <button 
          className="primary-button"
          onClick={handleConfirmMatches}
          disabled={loading}
        >
          {loading ? 'Confirming...' : 'Confirm & Continue'}
        </button>
      </div>
    </div>
  );
};

export default PartMatching;